<template>
  <div ref="containerRef" class="pdf-preview-container pdf-preview-pdfjs" :class="{ 'has-text-layer': highlightKeyword !== null }" :style="containerStyle">
    <div v-if="!loading && error" class="d-flex justify-content-center align-items-center w-100 h-100 text-danger text-center px-3">
      <div>
        <i class="fas fa-exclamation-triangle mb-2 d-block fa-2x"></i>
        <div>{{ error }}</div>
        <button
          type="button"
          class="btn btn-sm btn-outline-primary mt-3"
          :disabled="!canOpenPdfInNewTab"
          @click="openPdfInNewTab"
        >
          PDFを新しいタブで開く
        </button>
        <a
          v-if="showDirectFallbackLink"
          :href="src"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-sm btn-link mt-2"
        >
          直接リンクで開く（フォールバック）
        </a>
      </div>
    </div>
    <div v-else-if="!loading && isMobile" class="d-flex justify-content-center align-items-center w-100 h-100 text-center px-3">
      <div>
        <i class="fas fa-file-pdf mb-2 d-block fa-3x text-danger"></i>
        <p class="text-secondary small mb-3">PDFプレビューは外部ブラウザで表示します</p>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canOpenPdfInNewTab"
          @click="openPdfInNewTab"
        >
          PDFを表示する
        </button>
        <a
          v-if="showDirectFallbackLink"
          :href="src"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-link btn-sm d-block mt-2"
        >
          開けない場合は直接リンクを試す
        </a>
      </div>
    </div>
    <div
      v-show="!loading && !error && !isMobile"
      ref="viewerWrapRef"
      class="pdfjs-viewer-wrap"
    >
      <div ref="viewerContainerRef" class="pdfjs-viewer-container">
        <div ref="viewerEl" class="pdfViewer"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, ref, onMounted, onUnmounted, watch } from 'vue';
import axios from 'axios';
import { toNew } from '../lib/toNew';
import { splitSearchKeywordTerms } from '../lib/searchKeywordTerms';
import { mergeOverlappingRanges } from '../lib/highlightSegments';
// pdf.js ビューア CSS。JS から import することで webpack の .css ルール
// (postcss-loader 含む) を通り、ビルド時に全セレクタが .pdf-preview-pdfjs
// 配下へプレフィックスされる (webpack.mix.js の postcss-prefix-selector 設定)。
// SFC の <style> 内 @import では css-loader の importLoaders により
// postcss-loader を通らずプレフィックスされないため、ここで import する。
import 'pdfjs-dist/legacy/web/pdf_viewer.css';

const CMAP_URL = '/js/pdfjs/cmaps/';
const STANDARD_FONT_URL = '/js/pdfjs/standard_fonts/';
const WORKER_SRC = '/js/pdfjs/pdf.worker.min.js';
const WASM_URL = '/js/pdfjs/wasm/';

// ---- 検索語の色付け (全文検索の一致語) ----
// 正規化: NFKC → 旧字体を新字体へ → 小文字化 → カタカナ(U+30A1-U+30F6)をひらがなへ → 空白除去。
const KATAKANA_RE = /[\u30A1-\u30F6]/g;
const normalizeCluster = (str) => toNew(str.normalize('NFKC'))
  .toLowerCase()
  .replace(KATAKANA_RE, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60))
  .replace(/\s+/g, '');

// 検索語の正規化 (文字列単位で NFKC を掛ける)
const normalizeKeyword = (str) => normalizeCluster(String(str || ''));

// raw を「それ以上分割すると NFKC の結果が変わる」最小単位 (クラスタ) に区切る。
// NFKC(a + b) !== NFKC(a) + NFKC(b) のとき a と b は結合されるため同じクラスタにする
// (例: 半角 ｶ + ﾞ → ガ、e + 結合アクセント)。サロゲートペアは 1 コードポイントとして扱う。
// 戻り値: 正規化後の文字列 norm と、norm の各 UTF-16 単位が由来する raw の範囲 [start, end)。
const buildNormalizedMap = (raw) => {
  const cps = Array.from(raw);
  const rawPos = [];
  let pos = 0;
  for (const cp of cps) {
    rawPos.push(pos);
    pos += cp.length;
  }
  rawPos.push(pos);

  let norm = '';
  const mapStart = [];
  const mapEnd = [];
  let i = 0;
  while (i < cps.length) {
    let j = i + 1;
    let cluster = cps[i];
    while (j < cps.length) {
      const joined = (cluster + cps[j]).normalize('NFKC');
      if (joined === cluster.normalize('NFKC') + cps[j].normalize('NFKC')) break;
      cluster += cps[j];
      j += 1;
    }
    const n = normalizeCluster(cluster);
    for (let k = 0; k < n.length; k += 1) {
      mapStart.push(rawPos[i]);
      mapEnd.push(rawPos[j]);
    }
    norm += n;
    i = j;
  }
  return { norm, mapStart, mapEnd };
};

const clearHighlights = (root) => {
  const marks = Array.from(root.querySelectorAll('span.highlight'));
  const parents = new Set();
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parents.add(parent);
  });
  parents.forEach((parent) => parent.normalize());
};

const applyHighlights = (root, keyword) => {
  clearHighlights(root);
  // 検索語を区切りの文字で語に分け、語ごとに正規化する (正規化後に空になる語は捨てる)
  const needles = splitSearchKeywordTerms(keyword).map(normalizeKeyword).filter((needle) => needle);
  if (!needles.length) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  const nodeStarts = [];
  let raw = '';
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    nodeStarts.push(raw.length);
    raw += node.nodeValue;
    node = walker.nextNode();
  }
  if (!raw) return;

  const { norm, mapStart, mapEnd } = buildNormalizedMap(raw);
  // 一致範囲 (raw 上の [start, end)) を集める
  const collected = [];
  needles.forEach((needle) => {
    let from = 0;
    while (from <= norm.length - needle.length) {
      const idx = norm.indexOf(needle, from);
      if (idx < 0) break;
      collected.push([mapStart[idx], mapEnd[idx + needle.length - 1]]);
      from = idx + needle.length;
    }
  });
  if (!collected.length) return;

  // 開始位置の順に並べ、重なる範囲を 1 つにまとめる
  const ranges = mergeOverlappingRanges(collected);

  // ノードごとの断片に分解する
  const segments = nodes.map(() => []);
  ranges.forEach(([start, end]) => {
    for (let n = 0; n < nodes.length; n += 1) {
      const nStart = nodeStarts[n];
      const nEnd = nStart + nodes[n].nodeValue.length;
      const s = Math.max(start, nStart);
      const e = Math.min(end, nEnd);
      if (s < e) segments[n].push([s - nStart, e - nStart]);
    }
  });

  // 後ろの断片から分割する (元ノードが常に左側に残るためオフセットがずれない)
  segments.forEach((segs, n) => {
    segs.sort((a, b) => b[0] - a[0]);
    segs.forEach(([s, e]) => {
      const target = nodes[n];
      const middle = target.splitText(s);
      middle.splitText(e - s);
      const mark = document.createElement('span');
      mark.className = 'highlight';
      middle.parentNode.insertBefore(mark, middle);
      mark.appendChild(middle);
    });
  });
};

let pdfjsLibPromise = null;
const loadPdfjs = () => {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = (async () => {
      try {
        // pdf_viewer.mjs はトップレベルで globalThis.pdfjsLib を destructure するため、
        // 先にコア側を完了させてからビューアを import する必要がある
        const lib = await import('pdfjs-dist/legacy/build/pdf.min.mjs');
        const viewer = await import('pdfjs-dist/legacy/web/pdf_viewer.mjs');
        if (!lib.GlobalWorkerOptions.workerSrc) {
          lib.GlobalWorkerOptions.workerSrc = WORKER_SRC;
        }
        return { lib, viewer };
      } catch (e) {
        pdfjsLibPromise = null;
        throw e;
      }
    })();
  }
  return pdfjsLibPromise;
};

export default {
  name: 'PdfPreviewPdfjs',
  props: {
    src: { type: String, required: true },
    height: { type: String, default: '500px' },
    showToolbar: { type: Boolean, default: false },
    responsive: { type: Boolean, default: false },
    aspectRatio: { type: String, default: '210 / 297' },
    thumbnailUrl: { type: String, default: null },
    precomputedBlobUrl: { type: String, default: null },
    initialPage: { type: Number, default: null },
    // null (未指定): 文字の層を作らない (従来動作)。文字列 (空文字含む): 文字の層を作り、空でなければ一致語を色付けする
    highlightKeyword: { type: String, default: null }
  },
  emits: ['blob-ready', 'page-change'],
  setup(props, { emit }) {
    const resolvedAspectRatio = ref(null);
    const containerRef = ref(null);
    const viewerWrapRef = ref(null);
    const viewerContainerRef = ref(null);
    const viewerEl = ref(null);

    const loading = ref(true);
    const error = ref(null);
    const blobObjectUrl = ref(null);
    const ownedBlobUrl = ref(null);
    const isMobile = ref(false);
    const showDirectFallbackLink = ref(false);

    let pdfViewerInstance = null;
    let eventBusInstance = null;
    let linkServiceInstance = null;
    let currentPdfDocument = null;
    let currentLoadingTask = null;
    let resizeObserver = null;
    let resizeTimer = null;
    let refitQueued = false;
    let windowResizeHandler = null;
    let renderGen = 0;
    // 非表示のため applyInitialPage で設定できなかったページ。表示状態の refitPageWidth で反映する
    let pendingPage = null;

    const effectiveBlobUrl = computed(() => props.precomputedBlobUrl || blobObjectUrl.value);

    const canOpenPdfInNewTab = computed(() =>
      Boolean(effectiveBlobUrl.value || (showDirectFallbackLink.value && props.src))
    );

    const containerStyle = computed(() => {
      const style = {
        width: '100%',
        position: 'relative',
        backgroundColor: 'var(--bg-surface, #f8f9fa)'
      };
      if (props.responsive) {
        style.height = 'auto';
        style.aspectRatio = resolvedAspectRatio.value || props.aspectRatio;
      } else {
        style.height = props.height;
      }
      return style;
    });

    const checkMobile = () => {
      const ua = navigator.userAgent;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const isSmallScreen = window.innerWidth < 768;
      isMobile.value = isMobileUA || isSmallScreen;
    };

    const clearOwnedBlobUrl = () => {
      if (ownedBlobUrl.value) {
        URL.revokeObjectURL(ownedBlobUrl.value);
        ownedBlobUrl.value = null;
      }
      blobObjectUrl.value = null;
    };

    const destroyViewer = async () => {
      try {
        if (currentLoadingTask) {
          await currentLoadingTask.destroy().catch(() => {});
          currentLoadingTask = null;
        }
      } catch (_) { /* noop */ }
      try {
        if (currentPdfDocument) {
          await currentPdfDocument.destroy().catch(() => {});
          currentPdfDocument = null;
        }
      } catch (_) { /* noop */ }
      if (pdfViewerInstance) {
        try { pdfViewerInstance.cleanup(); } catch (_) { /* noop */ }
        try { pdfViewerInstance.setDocument(null); } catch (_) { /* noop */ }
        pdfViewerInstance = null;
      }
      if (linkServiceInstance) {
        try { linkServiceInstance.setDocument(null, null); } catch (_) { /* noop */ }
        linkServiceInstance = null;
      }
      eventBusInstance = null;
      pendingPage = null;
    };

    const ensureViewer = async () => {
      const { viewer: viewerMod } = await loadPdfjs();
      if (!viewerContainerRef.value || !viewerEl.value) return null;
      if (pdfViewerInstance) return viewerMod;

      eventBusInstance = new viewerMod.EventBus();
      linkServiceInstance = new viewerMod.PDFLinkService({ eventBus: eventBusInstance });
      pdfViewerInstance = new viewerMod.PDFViewer({
        container: viewerContainerRef.value,
        viewer: viewerEl.value,
        eventBus: eventBusInstance,
        linkService: linkServiceInstance,
        textLayerMode: props.highlightKeyword === null ? 0 : 1,
      });
      linkServiceInstance.setViewer(pdfViewerInstance);

      eventBusInstance.on('pagesinit', () => {
        if (pdfViewerInstance) {
          pdfViewerInstance.currentScaleValue = 'page-width';
          applyInitialPage();
        }
      });

      eventBusInstance.on('pagechanging', (evt) => {
        // 反映待ちのページがある間は、ページ 1 などの途中経過を外へ伝えない
        if (pendingPage !== null) return;
        emit('page-change', evt.pageNumber);
      });

      if (props.highlightKeyword !== null) {
        eventBusInstance.on('textlayerrendered', (evt) => {
          if (evt.error) return;
          const layer = evt.source?.div?.querySelector('.textLayer');
          if (layer) applyHighlights(layer, props.highlightKeyword);
        });
      }
      return viewerMod;
    };

    const applyInitialPage = () => {
      if (!pdfViewerInstance) return;
      const page = props.initialPage;
      if (!Number.isInteger(page) || page < 1) return;
      // 非表示のビューアに対してページを設定すると、pdf.js 内部の scrollIntoView() が
      // offsetParent なしで console.error を出すため、表示中のときだけ実行する。
      // pdf.js 自身も同じ container の offsetParent で表示判定している (pdf_viewer.mjs)。
      if (!viewerContainerRef.value || !viewerContainerRef.value.offsetParent) {
        pendingPage = page;
        return;
      }
      pendingPage = null;
      pdfViewerInstance.currentPageNumber = page;
    };

    const fetchPdfBlob = async () => {
      if (props.precomputedBlobUrl) {
        const res = await fetch(props.precomputedBlobUrl);
        return await res.arrayBuffer();
      }
      const response = await axios.get(props.src, { responseType: 'blob' });
      const sourceBlob = response.data instanceof Blob ? response.data : new Blob([response.data]);
      const mimeType = (sourceBlob.type || '').toLowerCase();
      const blob = (mimeType === 'application/pdf' || mimeType.startsWith('application/pdf;'))
        ? sourceBlob
        : sourceBlob.slice(0, sourceBlob.size, 'application/pdf');

      const blobUrl = URL.createObjectURL(blob);
      ownedBlobUrl.value = blobUrl;
      blobObjectUrl.value = blobUrl;
      emit('blob-ready', blobUrl);

      return await blob.arrayBuffer();
    };

    const renderPdf = async () => {
      const myGen = ++renderGen;
      const isStale = () => myGen !== renderGen;

      checkMobile();
      error.value = null;
      showDirectFallbackLink.value = false;

      await destroyViewer();
      if (isStale()) return;
      clearOwnedBlobUrl();

      loading.value = true;
      let arrayBuffer = null;
      try {
        arrayBuffer = await fetchPdfBlob();
      } catch (err) {
        if (isStale()) return;
        console.error('Failed to load PDF:', err);
        error.value = 'PDFの読み込みに失敗しました';
        showDirectFallbackLink.value = Boolean(props.src);
        loading.value = false;
        return;
      }
      if (isStale()) return;

      if (isMobile.value) {
        loading.value = false;
        return;
      }

      try {
        const { lib } = await loadPdfjs();
        if (isStale()) return;
        await nextTick();
        if (isStale()) return;
        await ensureViewer();
        if (isStale()) return;

        currentLoadingTask = lib.getDocument({
          data: new Uint8Array(arrayBuffer),
          cMapUrl: CMAP_URL,
          cMapPacked: true,
          standardFontDataUrl: STANDARD_FONT_URL,
          wasmUrl: WASM_URL,
        });
        const pdfDocument = await currentLoadingTask.promise;
        if (isStale()) {
          try { await pdfDocument.destroy(); } catch (_) { /* noop */ }
          return;
        }
        currentPdfDocument = pdfDocument;

        if (props.thumbnailUrl == null) {
          try {
            const firstPage = await pdfDocument.getPage(1);
            const vp = firstPage.getViewport({ scale: 1 });
            if (vp.width > 0 && vp.height > 0) {
              resolvedAspectRatio.value = `${vp.width} / ${vp.height}`;
            }
          } catch (_) { /* noop */ }
        }
        if (isStale()) return;

        if (pdfViewerInstance) {
          pdfViewerInstance.setDocument(pdfDocument);
        }
        if (linkServiceInstance) {
          linkServiceInstance.setDocument(pdfDocument, null);
        }
      } catch (err) {
        if (isStale()) return;
        console.error('PDF.js render failed:', err);
        error.value = 'PDFの描画に失敗しました';
        showDirectFallbackLink.value = Boolean(props.src);
      } finally {
        if (!isStale()) {
          loading.value = false;
        }
      }
    };

    const openPdfInNewTab = () => {
      const primaryUrl = effectiveBlobUrl.value;
      if (primaryUrl) {
        const openedWindow = window.open(primaryUrl, '_blank', 'noopener,noreferrer');
        if (openedWindow) return;
        showDirectFallbackLink.value = Boolean(props.src);
      }
      if (showDirectFallbackLink.value && props.src) {
        window.open(props.src, '_blank', 'noopener,noreferrer');
      }
    };

    const refitPageWidth = () => {
      if (!pdfViewerInstance) return;
      // 非表示のビューアに対してスケールを設定すると、pdf.js 内部の scrollIntoView() が
      // offsetParent なしで console.error を出すため、表示中のときだけ実行する。
      // pdf.js 自身も同じ container の offsetParent で表示判定している (pdf_viewer.mjs)。
      if (!viewerContainerRef.value || !viewerContainerRef.value.offsetParent) return;
      if (!pdfViewerInstance.pagesCount) return;
      const keepPage = pendingPage !== null ? pendingPage : pdfViewerInstance.currentPageNumber;
      try {
        pdfViewerInstance.currentScaleValue = 'page-width';
      } catch (_) { /* noop */ }
      pendingPage = null;
      try {
        if (pdfViewerInstance.currentPageNumber !== keepPage) {
          pdfViewerInstance.currentPageNumber = keepPage;
        }
      } catch (_) { /* noop */ }
    };

    // 最初の 1 回は即時に倍率を合わせ(拡大ボタン等の一度きりの変化でちらつかせない)、
    // 140ms 以内に続く呼び出しはペイン境界ドラッグ中の再描画を抑えるため最後に 1 回へまとめる。
    const startRefitWait = () => {
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        if (refitQueued) {
          refitQueued = false;
          refitPageWidth();
        }
      }, 140);
    };

    const scheduleRefit = () => {
      if (resizeTimer) {
        refitQueued = true;
        clearTimeout(resizeTimer);
        startRefitWait();
        return;
      }
      refitPageWidth();
      startRefitWait();
    };

    const observeResize = () => {
      if (typeof ResizeObserver !== 'undefined' && containerRef.value) {
        resizeObserver = new ResizeObserver(() => scheduleRefit());
        resizeObserver.observe(containerRef.value);
        return;
      }
      windowResizeHandler = () => scheduleRefit();
      window.addEventListener('resize', windowResizeHandler);
    };

    const resolveAspectRatioFromThumbnail = () => {
      if (!props.thumbnailUrl) return;
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          resolvedAspectRatio.value = `${img.naturalWidth} / ${img.naturalHeight}`;
        }
      };
      img.src = props.thumbnailUrl;
    };

    watch(() => props.src, (newSrc, oldSrc) => {
      if (newSrc && newSrc !== oldSrc) {
        renderPdf();
      }
    });

    watch(() => props.precomputedBlobUrl, () => {
      renderPdf();
    });

    watch(() => props.thumbnailUrl, () => {
      resolvedAspectRatio.value = null;
      resolveAspectRatioFromThumbnail();
    });

    watch(
      () => [props.height, props.responsive, props.aspectRatio],
      () => scheduleRefit()
    );

    watch(() => props.highlightKeyword, (kw) => {
      if (kw === null || !viewerEl.value) return;
      viewerEl.value.querySelectorAll('.textLayer').forEach((layer) => applyHighlights(layer, kw));
    });

    watch(() => props.initialPage, () => {
      if (pdfViewerInstance) {
        applyInitialPage();
      }
    });

    onMounted(() => {
      resolveAspectRatioFromThumbnail();
      renderPdf();
      nextTick(() => observeResize());
    });

    onUnmounted(async () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (windowResizeHandler) window.removeEventListener('resize', windowResizeHandler);
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = null;
      refitQueued = false;
      await destroyViewer();
      clearOwnedBlobUrl();
    });

    return {
      containerRef,
      viewerWrapRef,
      viewerContainerRef,
      viewerEl,
      containerStyle,
      loading,
      error,
      isMobile,
      canOpenPdfInNewTab,
      openPdfInNewTab,
      showDirectFallbackLink,
      src: computed(() => props.src),
    };
  }
};
</script>

<style scoped>
.pdfjs-viewer-wrap {
  position: absolute;
  inset: 0;
}
.pdfjs-viewer-container {
  position: absolute;
  inset: 0;
  overflow: auto;
  background-color: var(--bg-surface, #f8f9fa);
}
</style>
